import React from "react"
import {
  faArrowTrendUp,
  faChartLine,
  faCoins,
  faFileInvoiceDollar,
  faMotorcycle,
  faReceipt,
  faSackDollar,
  faShop,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { AdminFinancialMetrics } from "../../../Objects/AdminDashboard/AdminFinancialMetrics"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { SeriesChart } from "../components/SeriesChart"

interface FinancialMetricsSectionProps {
  financialMetrics: AdminFinancialMetrics
  monthlyOrdersAmountSeries: { label: string; value: number }[]
  monthlyPlatformFeeSeries: { label: string; value: number }[]
  monthlyManualExternalOrdersSeries: { label: string; value: number }[]
}

export const FinancialMetricsSection: React.FC<FinancialMetricsSectionProps> = ({
  financialMetrics,
  monthlyOrdersAmountSeries,
  monthlyPlatformFeeSeries,
  monthlyManualExternalOrdersSeries,
}) => {
  const { t } = useTranslation("frontend")

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1800px]:grid-cols-5">
        <AdminMetricCard
          title={t("admin_dashboard.financial_total_paid_orders")}
          value={`${financialMetrics.summary.totalPaidOrders}`}
          icon={faReceipt}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_total_orders_movement")}
          value={convertToLocalMoney(financialMetrics.summary.totalOrdersAmount)}
          icon={faArrowTrendUp}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_orders_count")}
          value={`${financialMetrics.summary.manualExternalOrdersCount}`}
          icon={faFileInvoiceDollar}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_order_customers_count")}
          value={`${financialMetrics.summary.manualExternalOrderCustomersCount}`}
          icon={faShop}
          tone="violet"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_orders_amount")}
          value={convertToLocalMoney(financialMetrics.summary.manualExternalOrdersAmount)}
          icon={faFileInvoiceDollar}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.manual_external_orders_net_amount")}
          value={convertToLocalMoney(financialMetrics.summary.manualExternalOrdersNetAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_total_split")}
          value={convertToLocalMoney(financialMetrics.summary.totalSplitAmount)}
          icon={faCoins}
          tone="violet"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_platform_fee_gain")}
          value={convertToLocalMoney(financialMetrics.summary.totalPlatformFeeAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_restaurant_transfer")}
          value={convertToLocalMoney(financialMetrics.summary.totalRestaurantAmount)}
          icon={faShop}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_courier_transfer")}
          value={convertToLocalMoney(financialMetrics.summary.totalCourierAmount)}
          icon={faMotorcycle}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_average_order_ticket")}
          value={convertToLocalMoney(financialMetrics.summary.averageOrderTicket)}
          icon={faChartLine}
          tone="violet"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_average_platform_take_rate")}
          value={`${financialMetrics.summary.platformTakeRatePercentage.toFixed(2)}%`}
          icon={faArrowTrendUp}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_current_month_paid_orders")}
          value={`${financialMetrics.summary.currentMonthPaidOrders}`}
          icon={faReceipt}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.current_month_manual_external_orders_count")}
          value={`${financialMetrics.summary.currentMonthManualExternalOrdersCount}`}
          icon={faFileInvoiceDollar}
          tone="info"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_current_month_movement")}
          value={convertToLocalMoney(financialMetrics.summary.currentMonthOrdersAmount)}
          icon={faArrowTrendUp}
          tone="brand"
        />

        <AdminMetricCard
          title={t("admin_dashboard.current_month_manual_external_orders_net_amount")}
          value={convertToLocalMoney(financialMetrics.summary.currentMonthManualExternalOrdersNetAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_current_month_platform_fee")}
          value={convertToLocalMoney(financialMetrics.summary.currentMonthPlatformFeeAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_approved_splits")}
          value={`${financialMetrics.summary.approvedSplitsCount}`}
          icon={faCoins}
          tone="positive"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_pending_splits")}
          value={`${financialMetrics.summary.pendingSplitsCount}`}
          icon={faCoins}
          tone="warning"
        />

        <AdminMetricCard
          title={t("admin_dashboard.financial_rejected_splits")}
          value={`${financialMetrics.summary.rejectedSplitsCount}`}
          icon={faCoins}
          tone="negative"
        />
      </div>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
        <div className="mt-4 grid grid-cols-1 gap-4 p-1 2xl:grid-cols-2 min-[2200px]:grid-cols-3">
          <SeriesChart
            title={t("admin_dashboard.financial_monthly_orders_movement")}
            data={monthlyOrdersAmountSeries}
            money={true}
          />

          <SeriesChart
            title={t("admin_dashboard.financial_monthly_platform_gain")}
            data={monthlyPlatformFeeSeries}
            money={true}
          />

          <SeriesChart
            title={t("admin_dashboard.monthly_manual_external_orders_net")}
            data={monthlyManualExternalOrdersSeries}
            money={true}
          />
        </div>
      </Animate>
    </div>
  )
}
